import { useMemo } from "react";

import { integrationAuthProvider } from "../../../../constants/authSource";
import { useGlobalStore } from "../../../../context/UserContext/userContext";

/**
 * Auth snapshot sent to CRE (handshake + mirror-state).
 * Extend this object when passing tokens or permissions later — keep keys stable.
 *
 * @typedef {object} CrePageAuth
 * @property {"cre"|"radical"} provider
 * @property {boolean} isCreAuth Same as `provider === "cre"`.
 * @property {string} [userId] Active Medilis user id when available.
 * @property {string} [accessToken] Reserved — wire when extension needs API access (do not log in extension).
 * @property {Record<string, unknown>} [extras] Feature flags for the extension: `isAdmin` + `permissions`
 *   (CRE licence codes from Validate) so the mirror can lock actions the user may not run.
 */

/**
 * @returns {CrePageAuth}
 */
export function buildCrePageAuth({
  authSource,
  creUser,
  radicalUser,
}) {
  const provider = integrationAuthProvider(authSource);
  const user = provider === "cre" ? creUser : radicalUser;

  return {
    provider,
    isCreAuth: provider === "cre",
    userId:
      user?.userId != null ? String(user.userId) : undefined,
    // accessToken — intentionally omitted until extension auth flow is defined.
    extras: {
      isAdmin: provider === "cre" ? creUser?.isAdmin === true : true,
      permissions:
        provider === "cre" && Array.isArray(creUser?.permissions)
          ? creUser.permissions
          : null,
    },
  };
}

/** React hook: current page auth for CRE extension protocol. */
export function useCrePageAuth() {
  const authSource = useGlobalStore((s) => s.authSource);
  const creUser = useGlobalStore((s) => s.creUser);
  const radicalUser = useGlobalStore((s) => s.radicalUser);
  return useMemo(
    () => buildCrePageAuth({ authSource, creUser, radicalUser }),
    [authSource, creUser, radicalUser],
  );
}
